import { memo } from 'react';
import {
  alpha,
  Badge,
  Chip,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from '@mui/material';
import { NavLink } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useAuth } from '@/shared/auth/useAuth';
import type { Role } from '@/shared/auth/roles';

export interface NavItemProps {
  readonly to: string;
  readonly label: string;
  readonly icon?: ReactNode;
  readonly requiresRole?: Role;
  /**
   * Forwarded to `NavLink`'s own `end` prop — require an exact match rather
   * than the default URL-prefix match. Needed only when `to` is a prefix of
   * a sibling nav item's route (see `NavItemConfig.end`'s doc comment).
   */
  readonly end?: boolean;
  /** When true, the item renders icon-only with a tooltip showing the label. */
  readonly collapsed?: boolean;
  /** Unread/new-item count shown as a badge. Omitted or 0 renders no badge. */
  readonly badgeContent?: number;
  /** aria-label for the badge. */
  readonly badgeLabel?: string;
}

/**
 * Sidebar navigation link with role-gated visibility.
 * Renders nothing when the active user lacks `requiresRole`.
 *
 * When `collapsed` is true the link shrinks to icon-only and the label
 * surfaces as a hover tooltip — used by `AppShell`'s mini-variant drawer.
 *
 * `badgeContent` renders differently depending on available space: expanded
 * (label visible) shows a trailing pill after the label; collapsed
 * (icon-only mini rail, no room for a pill) falls back to the corner-dot
 * `Badge` wrapping the icon.
 */
const NavItemComponent = ({
  to,
  label,
  icon,
  requiresRole,
  end,
  collapsed = false,
  badgeContent,
  badgeLabel,
}: NavItemProps): JSX.Element | null => {
  const { hasRole } = useAuth();

  if (requiresRole && !hasRole(requiresRole)) {
    return null;
  }

  const hasBadge = badgeContent !== undefined && badgeContent > 0;
  const badgeAriaLabel = badgeLabel ?? `${badgeContent ?? 0} new`;
  const badgeDisplay = hasBadge && badgeContent! > 99 ? '99+' : badgeContent;

  const displayIcon =
    icon && hasBadge && collapsed ? (
      <Badge
        color="error"
        badgeContent={badgeContent}
        max={99}
        overlap="circular"
        aria-label={badgeAriaLabel}
        // Scaled down 1:1 with the 16px nav icon (CPA-6) — MUI's standard
        // Badge default (20px diameter / 12px font) was sized against the
        // PREVIOUS 20px icon; left alone it would now render larger than
        // the icon it decorates. Font size matches the expanded-row count
        // Chip below for visual-family consistency.
        //
        // Set via `slotProps.badge.style` (an inline style on the badge
        // span), not `sx`'s `'& .MuiBadge-badge'`: that nested-selector form
        // measured as LOSING to MUI's own base `.MuiBadge-badge` rule
        // despite being the objectively more specific selector (verified by
        // dumping the generated stylesheets) — MUI's Badge badge slot is
        // rendered through its own styled-component insertion point, which
        // isn't guaranteed to come before an sx-nested-selector's rule in
        // DOM order, and ties/near-ties in class-selector specificity make
        // the outcome insertion-order-dependent. An inline style sidesteps
        // that entirely.
        slotProps={{
          badge: {
            style: { height: 16, minWidth: 16, fontSize: 10, fontWeight: 700 },
          },
        }}
      >
        {icon}
      </Badge>
    ) : (
      icon
    );

  const button = (
    <ListItemButton
      component={NavLink}
      to={to}
      end={end}
      sx={{
        minHeight: 35,
        borderRadius: 1,
        justifyContent: collapsed ? 'center' : 'flex-start',
        px: collapsed ? 1.5 : 1.25,
        py: 0.875,
        color: (theme) => theme.palette.grey[500],
        // Matches the design source's active nav treatment — a light-blue
        // tint + blue ink, not the generic grey MUI `action.selected`.
        '&.active': {
          backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08),
          color: 'primary.main',
        },
        '&.active .MuiListItemIcon-root': { color: 'primary.main' },
        // Touch-target floor (CLAUDE.md responsive rules): 35px is below the
        // mandatory 44x44 CSS px minimum on phone/tablet, where the drawer
        // renders full-width (AppShell forces `collapsed=false` below `md`,
        // so this never fights the desktop mini-rail). Fine-pointer desktop
        // rendering stays pixel-exact to the 35px spec. Same pattern as
        // `client-success/touchTarget.ts`'s `TOUCH_TARGET_TEXT`, inlined
        // here rather than imported since that module is a different
        // feature's constant.
        '@media (pointer: coarse)': { minHeight: 44 },
      }}
    >
      {displayIcon ? (
        <ListItemIcon
          sx={{
            minWidth: collapsed ? 0 : 36,
            mr: collapsed ? 0 : 1.125,
            justifyContent: 'center',
            color: 'inherit',
          }}
        >
          {displayIcon}
        </ListItemIcon>
      ) : null}
      {!collapsed ? (
        <ListItemText
          primary={label}
          // `my: 0` cancels ListItemText's own default 4px top/bottom
          // margin — without it the row can't hit the spec's 35px height
          // even with the button's padding/line-height otherwise correct.
          sx={{ my: 0 }}
          primaryTypographyProps={{
            // Set directly on the label's own Typography, not just on the
            // ListItemButton: MUI's ListItemText renders its own
            // Typography, whose variant class sets `font-weight` itself —
            // that explicit declaration wins over a `font-weight` that only
            // reaches it via CSS inheritance from an ancestor. `color`,
            // which ListItemText does NOT set itself, cascades fine instead
            // (this is exactly why the audit's finding #26 measured the
            // active nav item as "text #0060e5/400" — the `.active` color
            // override below took effect, but the old `.active { fontWeight:
            // 600 }` never did; requirements-clients-page-audit.md line
            // 131). One weight (500) now covers both active and inactive
            // per spec, so no `.active`-specific override is needed any more.
            sx: { fontSize: 14, fontWeight: 500, lineHeight: '21px' },
          }}
        />
      ) : null}
      {!collapsed && hasBadge ? (
        <Chip
          label={badgeDisplay}
          color="error"
          size="small"
          aria-label={badgeAriaLabel}
          sx={{
            ml: 1,
            height: 14,
            '& .MuiChip-label': {
              fontSize: '10px',
              fontWeight: 700,
              lineHeight: 1,
              padding: '2px 6px',
            },
          }}
        />
      ) : null}
    </ListItemButton>
  );

  // Tooltip only when collapsed — otherwise the label is already visible
  // and a tooltip is noisy.
  return collapsed ? (
    <Tooltip title={label} placement="right" arrow>
      {button}
    </Tooltip>
  ) : (
    button
  );
};

export const NavItem = memo(NavItemComponent);
